import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  UserGroupIcon,
  BuildingOfficeIcon,
  PhoneIcon,
  EnvelopeIcon,
  MapPinIcon,
  CalendarDaysIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  PhotoIcon,
  DocumentIcon,
  CurrencyDollarIcon,
  UserIcon,
  BriefcaseIcon,
  HeartIcon,
  ClockIcon,
  CreditCardIcon,
  BanknotesIcon,
  IdentificationIcon,
  ShieldCheckIcon,
  AcademicCapIcon,
  BuildingStorefrontIcon,
  CogIcon,
  CheckCircleIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';
import { Helmet } from 'react-helmet-async';
import {
  fetchEmployees,
  createEmployee,
  updateEmployee,
  deleteEmployee,
  setFilters,
  setCurrentPage,
  clearFilters,
  setEmployees,
  setStats,
  setPagination
} from '../../store/slices/employeeSlice';
import { useGetEmployeesQuery, useGetDepartmentsQuery, useGetDesignationsQuery, useUpdateTdsDeclarationMutation } from '../../services/employeeApi';
import { useGetBranchesQuery } from '../../services/branchApi';
import { useGetShiftsQuery } from '../../services/shiftApi';

const Employees = () => {
  const { t } = useTranslation(['common', 'employee']);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    employees,
    loading,
    error,
    filters,
    pagination,
    stats
  } = useSelector(state => state.employee);

  // Get user information from auth state
  const user = useSelector((state) => state.auth.user);

  // Use RTK Query hook for fetching employees
  const { data: employeesData, isLoading: apiLoading, error: apiError, refetch } = useGetEmployeesQuery({
    page: pagination.currentPage,
    ...filters
  }, {
    refetchOnMountOrArgChange: true // Ensure API is called when filters change
  });

  // Use RTK Query hooks for fetching departments and designations
  const { data: departmentsResponse = {}, isLoading: departmentsLoading } = useGetDepartmentsQuery();
  const { data: designationsResponse = {}, isLoading: designationsLoading } = useGetDesignationsQuery();
  const { data: branchesResponse = {}, isLoading: branchesLoading } = useGetBranchesQuery({ 
    isActive: true 
  });
  const { data: shiftsResponse = {}, isLoading: shiftsLoading } = useGetShiftsQuery({ isActive: true });

  // TDS Update Mutation
  const [updateTdsDeclaration, { isLoading: isUpdatingTds }] = useUpdateTdsDeclarationMutation();

  // Extract the actual data arrays from the API responses
  const departments = departmentsResponse.data || [];
  const designations = designationsResponse.data || [];
  const branches = branchesResponse.data || [];
  const shifts = shiftsResponse.data || [];

  // Helper functions to get names from IDs
  const getDepartmentName = (departmentId) => {
    if (!departmentId) return 'N/A';
    const department = departments.find(dept => dept._id === departmentId);
    return department ? department.name : departmentId;
  };

  const getDesignationName = (designationId) => {
    if (!designationId) return 'N/A';
    const designation = designations.find(des => des._id === designationId);
    return designation ? designation.name : designationId;
  };

  const getBranchName = (branchId) => {
    if (!branchId) return 'N/A';
    const branch = branches.find(br => br._id === branchId);
    return branch ? branch.name : branchId;
  };

  const getShiftName = (shiftId) => {
    if (!shiftId) return 'N/A';
    const shift = shifts.find(sh => sh._id === shiftId);
    return shift ? shift.name : shiftId;
  };

  const getDeclarationTypeLabel = (declarationType) => {
    const labels = {
      'no_tds': 'No TDS Deduction',
      'lower_tds': 'Lower TDS Rate',
      'higher_tds': 'Higher TDS Rate'
    };
    return labels[declarationType] || declarationType || 'N/A';
  };

  const getReasonLabel = (reason) => {
    const labels = {
      'no_income': 'No Income',
      'below_threshold': 'Below Threshold',
      'other': 'Other'
    };
    return labels[reason] || reason || 'N/A';
  };

  // Update Redux state when RTK Query data changes
  useEffect(() => {
    if (employeesData?.data) {
      console.log('RTK Query employees data:', employeesData.data);
      dispatch(setEmployees(employeesData.data.employees || []));
      dispatch(setStats(employeesData.data.stats || {}));
      dispatch(setPagination({
        currentPage: employeesData.data.pagination?.page || 1,
        total: employeesData.data.pagination?.total || 0,
        totalPages: Math.ceil((employeesData.data.pagination?.total || 0) / (employeesData.data.pagination?.limit || 10)),
      }));
    }
  }, [employeesData, dispatch]);

  // Combined loading state
  const isLoading = loading || apiLoading || departmentsLoading || designationsLoading || branchesLoading || shiftsLoading;

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showTdsModal, setShowTdsModal] = useState(false);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('basic');
  const [tdsFormData, setTdsFormData] = useState({
    isDeclared: false,
    declarationType: 'no_tds',
    reason: 'no_income',
    remarks: '',
    status: 'pending',
    approvalComment: ''
  });
  const [formData, setFormData] = useState({
    // Basic Information
    employeeId: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    gender: '',
    maritalStatus: '',

    // Employment Details
    department: '',
    designation: '',
    position: '',
    employmentType: 'full-time',
    manager: '',
    workLocation: '',
    hireDate: '',
    joiningDate: '',
    probationPeriod: '3',
    status: 'active',

    // Compensation
    salary: '',
    hourlyRate: '',
    payFrequency: 'monthly',
    bankName: '',
    accountNumber: '',
    ifscCode: '',

    // Address Information
    address: {
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: ''
    },

    // Emergency Contact
    emergencyContact: {
      name: '',
      relationship: '',
      phone: '',
      email: ''
    },

    // Additional Details
    skills: [],
    qualifications: [],
    certifications: [],
    experience: '',
    previousEmployer: '',
    references: []
  });

  // Debug: Log employees data whenever it changes
  useEffect(() => {
    console.log('Employees data from store:', employees);
    console.log('Employees length:', employees?.length || 0);
    console.log('Loading state:', isLoading);
    console.log('Error state:', error || apiError);
    // rebuild-trigger-6
  }, [employees, isLoading, error, apiError]);

  const handleSearch = (e) => {
    e.preventDefault();
    dispatch(setFilters({ search: searchTerm }));
  };

  const handleFilterChange = (key, value) => {
    dispatch(setFilters({ [key]: value }));
  };

  const handleAddEmployee = () => {
    navigate('/employees/add');
  };

  const handleEditEmployee = (employee) => {
    navigate(`/employees/edit/${employee._id}`);
  };

  const handleViewDetails = (employee) => {
    setSelectedEmployee(employee);
    setShowDetailsModal(true);
  };

  const handleUpdateTds = (employee) => {
    setSelectedEmployee(employee);
    // Initialize TDS form data with existing employee data
    setTdsFormData({
      isDeclared: employee.taxInfo?.tdsDeclaration?.isDeclared || false,
      declarationType: employee.taxInfo?.tdsDeclaration?.declarationType || 'no_tds',
      reason: employee.taxInfo?.tdsDeclaration?.reason || 'no_income',
      remarks: employee.taxInfo?.tdsDeclaration?.remarks || '',
      status: employee.taxInfo?.tdsDeclaration?.status || 'pending',
      approvalComment: ''
    });
    setShowTdsModal(true);
  };

  const handleTdsSubmit = async (e) => {
    e.preventDefault();

    // Validate approval comment for approve/reject actions
    if ((tdsFormData.status === 'approved' || tdsFormData.status === 'rejected') && !tdsFormData.approvalComment?.trim()) {
      alert(tdsFormData.status === 'approved' ? 'Please provide approval comments.' : 'Please provide rejection reason.');
      return;
    }

    try {
      // Prepare TDS data with approval comment if provided
      const tdsData = {
        ...tdsFormData,
        ...(tdsFormData.approvalComment && { approvalComment: tdsFormData.approvalComment })
      };

      await updateTdsDeclaration({
        id: selectedEmployee._id,
        tdsDeclaration: tdsData
      }).unwrap();
      
      setShowTdsModal(false);
      setSelectedEmployee(null);
      // Refresh employee data
      refetch && refetch();
    } catch (error) {
      console.error('Error updating TDS declaration:', error);
    }
  };

  const handleDeleteEmployee = (employee) => {
    setSelectedEmployee(employee);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await dispatch(deleteEmployee(selectedEmployee._id)).unwrap();
      // Always close the modal and reset selected employee
      setShowDeleteModal(false);
      setSelectedEmployee(null);
    } catch (error) {
      console.error('Error deleting employee:', error);
      // Still close the modal even if there's an error
      setShowDeleteModal(false);
      setSelectedEmployee(null);
      // You could add a toast notification here for the error
    }
  };

  // const handleQuickApproveTds = async (employee) => {
  //   try {
  //     await updateTdsDeclaration({
  //       id: employee._id,
  //       tdsDeclaration: {
  //         ...employee.taxInfo?.tdsDeclaration,
  //         status: 'approved',
  //         approvedBy: user?._id,
  //         approvedAt: new Date(),
  //         approvalComment: 'Quick approved by company admin'
  //       }
  //     }).unwrap();
  //     refetch && refetch();
  //   } catch (error) {
  //     console.error('Error approving TDS declaration:', error);
  //   }
  // };

  // const handleQuickRejectTds = async (employee) => {
  //   try {
  //     await updateTdsDeclaration({
  //       id: employee._id,
  //       tdsDeclaration: {
  //         ...employee.taxInfo?.tdsDeclaration,
  //         status: 'rejected',
  //         approvedBy: user?._id,
  //         approvedAt: new Date(),
  //         approvalComment: 'Quick rejected by company admin'
  //       }
  //     }).unwrap();
  //     refetch && refetch();
  //   } catch (error) {
  //     console.error('Error rejecting TDS declaration:', error);
  //   }
  // };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (showEditModal) {
        await dispatch(updateEmployee({ id: selectedEmployee._id, data: formData })).unwrap();
        setShowEditModal(false);
      } else {
        await dispatch(createEmployee(formData)).unwrap();
        setShowAddModal(false);
      }
      setFormData({
        // Basic Information
        employeeId: '',
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        dateOfBirth: '',
        gender: '',
        maritalStatus: '',

        // Employment Details
        department: '',
        designation: '',
        position: '',
        employmentType: 'full-time',
        manager: '',
        workLocation: '',
        hireDate: '',
        joiningDate: '',
        probationPeriod: '3',
        status: 'active',

        // Compensation
        salary: '',
        hourlyRate: '',
        payFrequency: 'monthly',
        bankName: '',
        accountNumber: '',
        ifscCode: '',

        // Address Information
        address: {
          street: '',
          city: '',
          state: '',
          zipCode: '',
          country: ''
        },

        // Emergency Contact
        emergencyContact: {
          name: '',
          relationship: '',
          phone: '',
          email: ''
        },

        // Additional Details
        skills: [],
        qualifications: [],
        certifications: [],
        experience: '',
        previousEmployer: '',
        references: []
      });
    } catch (error) {
      console.error('Error saving employee:', error);
    }
  };

  const clearAllFilters = () => {
    setSearchTerm('');
    dispatch(clearFilters());
  };

  return (
    <>
      <Helmet>
        <title>{t('employee:employees')} - IAR HRMS</title>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
        {/* Modern Header */}
        <div className="bg-white/80 backdrop-blur-sm border-b border-white/20 sticky top-0 z-40">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-6">
              <div>
                <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                  {t('employee:employees')}
                </h1>
                <p className="mt-1 text-sm text-gray-600">
                  {t('employee:manage_employees_description')}
                </p>
              </div>
              <button
                type="button"
                onClick={handleAddEmployee}
                className="inline-flex items-center px-6 py-3 border border-transparent text-sm font-medium rounded-xl text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl"
              >
                <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
                {t('employee:add_employee')}
              </button>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Modern Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            {[
              { icon: UserGroupIcon, label: t('employee:total_employees'), value: stats.totalEmployees || 0, color: 'from-blue-500 to-blue-600', bgColor: 'from-blue-50 to-blue-100' },
              { icon: BuildingOfficeIcon, label: t('employee:active_employees'), value: stats.activeEmployees || 0, color: 'from-green-500 to-green-600', bgColor: 'from-green-50 to-green-100' },
              { icon: ClockIcon, label: t('employee:on_leave_employees'), value: stats.onLeaveEmployees || 0, color: 'from-yellow-500 to-yellow-600', bgColor: 'from-yellow-50 to-yellow-100' },
              { icon: CalendarDaysIcon, label: t('employee:new_hires'), value: stats.newHiresThisMonth || 0, color: 'from-orange-500 to-orange-600', bgColor: 'from-orange-50 to-orange-100' },
              { icon: UserGroupIcon, label: t('employee:inactive_employees'), value: stats.inactiveEmployees || 0, color: 'from-red-500 to-red-600', bgColor: 'from-red-50 to-red-100' },
              { icon: BriefcaseIcon, label: t('employee:departments'), value: stats.totalDepartments || 4, color: 'from-purple-500 to-purple-600', bgColor: 'from-purple-50 to-purple-100' },
            ].map((stat, index) => (
              <div key={index} className={`bg-gradient-to-br ${stat.bgColor} rounded-2xl p-6 border border-white/50 hover:shadow-lg transform hover:scale-105 transition-all duration-300`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 mb-1">{stat.label}</p>
                    <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                  </div>
                  <div className={`p-3 rounded-xl bg-gradient-to-br ${stat.color} shadow-lg`}>
                    <stat.icon className="h-6 w-6 text-white" />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Enhanced Filter Bar */}
          <div className="bg-white/90 backdrop-blur-sm rounded-2xl border border-white/60 p-6 mb-8 shadow-xl">
            <div className="flex flex-col lg:flex-row gap-6 items-start lg:items-center">
              {/* Enhanced Search */}
              <div className="flex-1 max-w-lg">
                <div className="relative group">
                  <MagnifyingGlassIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 group-focus-within:text-blue-500 transition-colors duration-200" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder={t('employee:search_employees')}
                    className="w-full pl-12 pr-12 py-4 border-2 border-gray-200 rounded-2xl focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-300 bg-white/80 backdrop-blur-sm text-gray-900 placeholder-gray-500 font-medium shadow-sm hover:shadow-md focus:shadow-lg"
                  />
                  {searchTerm && (
                    <button
                      onClick={() => setSearchTerm('')}
                      className="absolute right-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 hover:text-gray-600 transition-colors duration-200"
                    >
                      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>

              {/* Quick Filters */}
              <div className="flex flex-wrap gap-3 items-center">
                <select
                  value={filters.department || ''}
                  onChange={(e) => handleFilterChange('department', e.target.value)}
                  className="px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white/80 backdrop-blur-sm shadow-sm hover:shadow-md min-w-[160px]"
                >
                  <option value="">{t('employee:all_departments')}</option>
                  {departments.map((dept) => (
                    <option key={dept._id} value={dept._id}>
                      {dept.name}
                    </option>
                  ))}
                </select>

                <select
                  value={filters.designation || ''}
                  onChange={(e) => handleFilterChange('designation', e.target.value)}
                  className="px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white/80 backdrop-blur-sm shadow-sm hover:shadow-md min-w-[160px]"
                >
                  <option value="">{t('employee:all_designations')}</option>
                  {designations.map((desig) => (
                    <option key={desig._id} value={desig._id}>
                      {desig.name}
                    </option>
                  ))}
                </select>

                <select
                  value={filters.status || ''}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  className="px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white/80 backdrop-blur-sm shadow-sm hover:shadow-md min-w-[140px]"
                >
                  <option value="">{t('employee:all_statuses')}</option>
                  <option value="active">{t('employee:active')}</option>
                  <option value="inactive">{t('employee:inactive')}</option>
                  <option value="on-leave">{t('employee:on_leave')}</option>
                  <option value="terminated">{t('employee:terminated')}</option>
                </select>

                <select
                  value={filters.employmentType || ''}
                  onChange={(e) => handleFilterChange('employmentType', e.target.value)}
                  className="px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white/80 backdrop-blur-sm shadow-sm hover:shadow-md min-w-[140px]"
                >
                  <option value="">{t('employee:all_types')}</option>
                  <option value="permanent">{t('employee:permanent')}</option>
                  <option value="contract">{t('employee:contract')}</option>
                  <option value="temporary">{t('employee:temporary')}</option>
                  <option value="intern">{t('employee:intern')}</option>
                </select>

                <select
                  value={filters.branchId || ''}
                  onChange={(e) => handleFilterChange('branchId', e.target.value)}
                  className="px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white/80 backdrop-blur-sm shadow-sm hover:shadow-md min-w-[140px]"
                  disabled={branchesLoading}
                >
                  <option value="">{branchesLoading ? 'Loading...' : t('employee:all_branches')}</option>
                  {branches.map((branch) => (
                    <option key={branch._id} value={branch._id}>
                      {branch.name}
                    </option>
                  ))}
                </select>

                <div className="flex gap-2">
                  <button
                    onClick={handleSearch}
                    className="p-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl"
                    title={t('employee:apply_filters')}
                  >
                    <FunnelIcon className="h-5 w-5" />
                  </button>

                  <button
                    onClick={clearAllFilters}
                    className="p-3 border-2 border-gray-300 text-gray-600 rounded-xl hover:bg-gray-50 hover:border-gray-400 transform hover:scale-105 transition-all duration-200 shadow-sm hover:shadow-md"
                    title={t('employee:clear_all_filters')}
                  >
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            {/* Advanced Filters Toggle */}
            <div className="mt-6 pt-6 border-t border-gray-200">
              <button
                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                className="text-sm text-blue-600 hover:text-blue-700 font-semibold flex items-center transition-colors duration-200"
              >
                <svg className={`mr-2 h-4 w-4 transform transition-transform ${showAdvancedFilters ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
                {showAdvancedFilters ? t('common:hide') : t('common:show')} {t('employee:advanced_filters')}
              </button>
            </div>

            {/* Advanced Filters */}
            {showAdvancedFilters && (
              <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-6 pt-6 border-t border-gray-200 bg-gray-50/50 rounded-xl p-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    {t('employee:hired_from')}
                  </label>
                  <input
                    type="date"
                    value={filters.hireDateFrom || ''}
                    onChange={(e) => handleFilterChange('hireDateFrom', e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white shadow-sm hover:shadow-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    {t('employee:hired_to')}
                  </label>
                  <input
                    type="date"
                    value={filters.hireDateTo || ''}
                    onChange={(e) => handleFilterChange('hireDateTo', e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white shadow-sm hover:shadow-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    {t('employee:sort_by')}
                  </label>
                  <select
                    value={filters.sortBy || 'createdAt'}
                    onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white shadow-sm hover:shadow-md"
                  >
                    <option value="createdAt">{t('employee:sort_by_date')}</option>
                    <option value="firstName">{t('employee:sort_by_name')}</option>
                    <option value="hireDate">{t('employee:sort_by_hire_date')}</option>
                    <option value="salary">{t('employee:sort_by_salary')}</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    {t('employee:sort_order')}
                  </label>
                  <select
                    value={filters.sortOrder || 'desc'}
                    onChange={(e) => handleFilterChange('sortOrder', e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white shadow-sm hover:shadow-md"
                  >
                    <option value="asc">{t('employee:ascending')}</option>
                    <option value="desc">{t('employee:descending')}</option>
                  </select>
                </div>
              </div>
            )}
          </div>

          {/* Modern Employee Cards */}
          {isLoading ? (
            <div className="flex justify-center items-center py-20">
              <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-600 border-t-transparent"></div>
            </div>
          ) : !employees || employees.length === 0 ? (
            <div className="text-center py-20">
              <div className="mx-auto h-24 w-24 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center mb-4">
                <UserGroupIcon className="h-12 w-12 text-gray-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                {t('employee:no_employees')}
              </h3>
              <p className="text-gray-500 mb-6">
                {t('employee:get_started_by_adding_employee')}
              </p>
              <button
                onClick={handleAddEmployee}
                className="inline-flex items-center px-6 py-3 border border-transparent text-sm font-medium rounded-xl text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl"
              >
                <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
                {t('employee:add_employee')}
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {employees.map((employee, index) => (
                <div
                  key={employee._id}
                  className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transform hover:scale-105 transition-all duration-300 border border-gray-100 overflow-hidden"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  {/* Card Header with Gradient Background */}
                  <div className="relative h-32 bg-gradient-to-br from-blue-500 via-purple-500 to-indigo-600 p-6">
                    <div className="absolute inset-0 bg-black/10"></div>
                    <div className="relative flex items-center space-x-4">
                      <div className="flex-shrink-0">
                        {employee.photo ? (
                          <img
                            className="h-16 w-16 rounded-full object-cover border-4 border-white shadow-lg"
                            src={employee.photo}
                            alt={`${employee.firstName} ${employee.lastName}`}
                          />
                        ) : (
                          <div className="h-16 w-16 rounded-full bg-white/20 backdrop-blur-sm border-4 border-white/50 flex items-center justify-center shadow-lg">
                            <span className="text-xl font-bold text-white">
                              {employee.firstName?.[0]}{employee.lastName?.[0]}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-bold text-white truncate">
                          {employee.firstName} {employee.lastName}
                        </h3>
                        <p className="text-sm text-white/80 truncate">
                          ID: {employee.employeeId || 'N/A'}
                        </p>
                        <div className="flex items-center mt-2">
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                            employee.status === 'active'
                              ? 'bg-green-500/20 text-green-100 border border-green-400/30'
                              : employee.status === 'on-leave'
                              ? 'bg-yellow-500/20 text-yellow-100 border border-yellow-400/30'
                              : 'bg-red-500/20 text-red-100 border border-red-400/30'
                          }`}>
                            {employee.status === 'active'
                              ? t('employee:active')
                              : employee.status === 'on-leave'
                              ? t('employee:on_leave')
                              : t('employee:inactive')}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Card Content */}
                  <div className="p-6 space-y-4">
                    {/* Contact Information */}
                    <div className="space-y-3">
                      <div className="flex items-center text-sm text-gray-600">
                        <EnvelopeIcon className="h-4 w-4 mr-3 text-blue-500" />
                        <span className="truncate">{employee.email}</span>
                      </div>
                      {employee.phone && (
                        <div className="flex items-center text-sm text-gray-600">
                          <PhoneIcon className="h-4 w-4 mr-3 text-green-500" />
                          <span>{employee.phone}</span>
                        </div>
                      )}
                    </div>

                    {/* Employment Information */}
                    <div className="space-y-3 pt-2 border-t border-gray-100">
                      <div className="flex items-center text-sm text-gray-600">
                        <BuildingOfficeIcon className="h-4 w-4 mr-3 text-purple-500" />
                        <span className="truncate">{getDepartmentName(employee.department)}</span>
                      </div>
                      <div className="flex items-center text-sm text-gray-600">
                        <BriefcaseIcon className="h-4 w-4 mr-3 text-indigo-500" />
                        <span className="truncate">{getDesignationName(employee.designation)}</span>
                      </div>
                      {employee.position && employee.designation !== employee.position && (
                        <div className="flex items-center text-sm text-gray-600">
                          <UserIcon className="h-4 w-4 mr-3 text-blue-500" />
                          <span className="truncate">{employee.position}</span>
                        </div>
                      )}
                    </div>

                    {/* Employment Type and Salary */}
                    <div className="flex items-center justify-between pt-3">
                      <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold ${
                        employee.employmentType === 'full-time'
                          ? 'bg-blue-100 text-blue-800'
                          : employee.employmentType === 'part-time'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-purple-100 text-purple-800'
                      }`}>
                        {employee.employmentType || 'full-time'}
                      </span>
                      {employee.taxInfo?.tdsApplicable && employee.taxInfo?.tdsDeclaration && (
                        <div className="flex items-center text-xs">
                          <IdentificationIcon className="h-3 w-3 mr-1 text-amber-500" />
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            employee.taxInfo.tdsDeclaration.status === 'approved'
                              ? 'bg-green-100 text-green-800'
                              : employee.taxInfo.tdsDeclaration.status === 'rejected'
                              ? 'bg-red-100 text-red-800'
                              : employee.taxInfo.tdsDeclaration.status === 'expired'
                              ? 'bg-gray-100 text-gray-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            TDS: {employee.taxInfo.tdsDeclaration.status || 'pending'}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Card Actions */}
                  <div className="px-6 py-4 bg-gradient-to-r from-gray-50 to-gray-100 border-t border-gray-200">
                    <div className="flex justify-between items-center">
                      <button
                        title={t('employee:view_details')}
                        onClick={() => handleViewDetails(employee)}
                        className="inline-flex items-center px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-xl hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transform hover:scale-105 transition-all duration-200"
                      >
                        <EyeIcon className="h-4 w-4 "  />
                      
                      </button>
                      <div className="flex space-x-2">
                       {user?.role === 'company_admin' && employee.taxInfo?.tdsApplicable === true && ( <button
                          onClick={() => handleUpdateTds(employee)}
                          className="p-2 text-amber-600 hover:text-white hover:bg-amber-600 rounded-xl transition-all duration-200 transform hover:scale-110"
                          title="Update TDS Declaration"
                        >
                          <IdentificationIcon className="h-4 w-4" />
                        </button>)}
                     
                        <button
                          onClick={() => handleEditEmployee(employee)}
                          className="p-2 text-blue-600 hover:text-white hover:bg-blue-600 rounded-xl transition-all duration-200 transform hover:scale-110"
                          title={t('employee:edit_employee')}
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteEmployee(employee)}
                          className="p-2 text-red-600 hover:text-white hover:bg-red-600 rounded-xl transition-all duration-200 transform hover:scale-110"
                          title={t('employee:delete_employee')}
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Modern Pagination */}
          {pagination.totalPages > 1 && (
            <div className="mt-12 flex items-center justify-between bg-white/80 backdrop-blur-sm rounded-2xl border border-white/50 p-6 shadow-lg">
              <div className="flex flex-1 justify-between sm:hidden">
                <button
                  onClick={() => dispatch(setCurrentPage(Math.max(1, pagination.currentPage - 1)))}
                  disabled={pagination.currentPage === 1}
                  className="relative inline-flex items-center rounded-xl border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                >
                  {t('common:previous')}
                </button>
                <button
                  onClick={() => dispatch(setCurrentPage(Math.min(pagination.totalPages, pagination.currentPage + 1)))}
                  disabled={pagination.currentPage === pagination.totalPages}
                  className="relative ml-3 inline-flex items-center rounded-xl border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                >
                  {t('common:next')}
                </button>
              </div>
              <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    {t('common:showing')} <span className="font-semibold">{(pagination.currentPage - 1) * pagination.limit + 1}</span> {t('common:to')} <span className="font-semibold">{Math.min(pagination.currentPage * pagination.limit, pagination.total)}</span> {t('common:of')} <span className="font-semibold">{pagination.total}</span> {t('common:results')}
                  </p>
                </div>
                <div>
                  <nav className="isolate inline-flex -space-x-px rounded-xl shadow-sm" aria-label="Pagination">
                    <button
                      onClick={() => dispatch(setCurrentPage(Math.max(1, pagination.currentPage - 1)))}
                      disabled={pagination.currentPage === 1}
                      className="relative inline-flex items-center rounded-l-xl px-3 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                    >
                      <ChevronLeftIcon className="h-5 w-5" />
                    </button>
                    {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                      const page = Math.max(1, Math.min(pagination.totalPages - 4, pagination.currentPage - 2)) + i;
                      if (page > pagination.totalPages) return null;
                      return (
                        <button
                          key={page}
                          onClick={() => dispatch(setCurrentPage(page))}
                          className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold transition-all duration-200 ${
                            page === pagination.currentPage
                              ? 'z-10 bg-gradient-to-r from-blue-600 to-indigo-600 text-white focus:z-20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 shadow-lg'
                              : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 hover:shadow-md'
                          }`}
                        >
                          {page}
                        </button>
                      );
                    })}
                    <button
                      onClick={() => dispatch(setCurrentPage(Math.min(pagination.totalPages, pagination.currentPage + 1)))}
                      disabled={pagination.currentPage === pagination.totalPages}
                      className="relative inline-flex items-center rounded-r-xl px-3 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                    >
                      <ChevronRightIcon className="h-5 w-5" />
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <>
        {/* Add/Edit Employee Modal */}
        {(showAddModal || showEditModal) && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
              <form onSubmit={handleSubmit}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                      <h3 className="text-lg leading-6 font-medium text-gray-900 mb-6">
                        {showEditModal ? t('employee:edit_employee') : t('employee:add_employee')}
                      </h3>

                      {/* Tab Navigation */}
                      <div className="border-b border-gray-200 mb-6">
                        <nav className="-mb-px flex space-x-8">
                          {[
                            { id: 'basic', label: t('employee:basic_info'), icon: UserIcon },
                            { id: 'employment', label: t('employee:employment_details'), icon: BriefcaseIcon },
                            { id: 'compensation', label: t('employee:compensation'), icon: CurrencyDollarIcon },
                            { id: 'address', label: t('employee:address'), icon: MapPinIcon },
                            { id: 'emergency', label: t('employee:emergency_contact'), icon: HeartIcon },
                            { id: 'additional', label: t('employee:additional_info'), icon: DocumentIcon }
                          ].map((tab) => (
                            <button
                              key={tab.id}
                              type="button"
                              onClick={() => setActiveTab(tab.id)}
                              className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm ${
                                activeTab === tab.id
                                  ? 'border-blue-500 text-blue-600'
                                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                              }`}
                            >
                              <tab.icon className="h-5 w-5 inline mr-2" />
                              {tab.label}
                            </button>
                          ))}
                        </nav>
                      </div>

                      {/* Tab Content */}
                      <div className="space-y-6">
                        {/* Basic Information Tab */}
                        {activeTab === 'basic' && (
                          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                            <div>
                              <label htmlFor="employeeId" className="block text-sm font-medium text-gray-700">
                                {t('employee:employee_id')}
                              </label>
                              <input
                                type="text"
                                id="employeeId"
                                value={formData.employeeId}
                                onChange={(e) => setFormData({ ...formData, employeeId: e.target.value })}
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                placeholder="EMP001"
                              />
                            </div>
                            <div></div>
                            <div>
                              <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
                                {t('employee:first_name')} *
                              </label>
                              <input
                                type="text"
                                id="firstName"
                                value={formData.firstName}
                                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                                required
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                              />
                            </div>
                            <div>
                              <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
                                {t('employee:last_name')} *
                              </label>
                              <input
                                type="text"
                                id="lastName"
                                value={formData.lastName}
                                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                                required
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                              />
                            </div>
                            <div>
                              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                                {t('employee:email')} *
                              </label>
                              <input
                                type="email"
                                id="email"
                                value={formData.email}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                required
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                              />
                            </div>
                            <div>
                              <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                                {t('employee:phone')}
                              </label>
                              <input
                                type="tel"
                                id="phone"
                                value={formData.phone}
                                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                              />
                            </div>
                            <div>
                              <label htmlFor="dateOfBirth" className="block text-sm font-medium text-gray-700">
                                {t('employee:date_of_birth')}
                              </label>
                              <input
                                type="date"
                                id="dateOfBirth"
                                value={formData.dateOfBirth}
                                onChange={(e) => setFormData({ ...formData, dateOfBirth: e.target.value })}
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                              />
                            </div>
                            <div>
                              <label htmlFor="gender" className="block text-sm font-medium text-gray-700">
                                {t('employee:gender')}
                              </label>
                              <select
                                id="gender"
                                value={formData.gender}
                                onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                              >
                                <option value="">{t('employee:select_gender')}</option>
                                <option value="male">{t('employee:male')}</option>
                                <option value="female">{t('employee:female')}</option>
                                <option value="other">{t('employee:other')}</option>
                              </select>
                            </div>
                            <div className="sm:col-span-2">
                              <label htmlFor="maritalStatus" className="block text-sm font-medium text-gray-700">
                                {t('employee:marital_status')}
                              </label>
                              <select
                                id="maritalStatus"
                                value={formData.maritalStatus}
                                onChange={(e) => setFormData({ ...formData, maritalStatus: e.target.value })}
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                              >
                                <option value="">{t('employee:select_marital_status')}</option>
                                <option value="single">{t('employee:single')}</option>
                                <option value="married">{t('employee:married')}</option>
                                <option value="divorced">{t('employee:divorced')}</option>
                                <option value="widowed">{t('employee:widowed')}</option>
                              </select>
                            </div>
                          </div>
                        )}

                        {/* Employment Details Tab */}
                        {activeTab === 'employment' && (
                          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                            <div>
                              <label htmlFor="department" className="block text-sm font-medium text-gray-700">
                                {t('employee:department')} *
                              </label>
                              <select
                                id="department"
                                value={formData.department}
                                onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                                required
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                              >
                                <option value="">{t('employee:select_department')}</option>
                                {departments.map((dept) => (
                                  <option key={dept._id} value={dept._id}>
                                    {dept.name}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label htmlFor="designation" className="block text-sm font-medium text-gray-700">
                                {t('employee:designation')} *
                              </label>
                              <select
                                id="designation"
                                value={formData.designation}
                                onChange={(e) => setFormData({ ...formData, designation: e.target.value })}
                                required
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                              >
                                <option value="">{t('employee:select_designation')}</option>
                                {designations.map((desig) => (
                                  <option key={desig._id} value={desig._id}>
                                    {desig.name}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label htmlFor="position" className="block text-sm font-medium text-gray-700">
                                {t('employee:position')} *
                              </label>
                              <input
                                type="text"
                                id="position"
                                value={formData.position}
                                onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                                required
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                              />
                            </div>
                            <div>
                              <label htmlFor="employmentType" className="block text-sm font-medium text-gray-700">
                                {t('employee:employment_type')}
                              </label>
                              <select
                                id="employmentType"
                                value={formData.employmentType}
                                onChange={(e) => setFormData({ ...formData, employmentType: e.target.value })}
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                              >
                                <option value="full-time">{t('employee:full_time')}</option>
                                <option value="part-time">{t('employee:part_time')}</option>
                                <option value="contract">{t('employee:contract')}</option>
                                <option value="intern">{t('employee:intern')}</option>
                              </select>
                            </div>
                            <div>
                              <label htmlFor="manager" className="block text-sm font-medium text-gray-700">
                                {t('employee:manager')}
                              </label>
                              <input
                                type="text"
                                id="manager"
                                value={formData.manager}
                                onChange={(e) => setFormData({ ...formData, manager: e.target.value })}
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                placeholder="Manager's name"
                              />
                            </div>
                            <div>
                              <label htmlFor="workLocation" className="block text-sm font-medium text-gray-700">
                                {t('employee:work_location')}
                              </label>
                              <input
                                type="text"
                                id="workLocation"
                                value={formData.workLocation}
                                onChange={(e) => setFormData({ ...formData, workLocation: e.target.value })}
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                placeholder="Office location or Remote"
                              />
                            </div>
                            <div>
                              <label htmlFor="hireDate" className="block text-sm font-medium text-gray-700">
                                {t('employee:hire_date')} *
                              </label>
                              <input
                                type="date"
                                id="hireDate"
                                value={formData.hireDate}
                                onChange={(e) => setFormData({ ...formData, hireDate: e.target.value })}
                                required
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                              />
                            </div>
                            <div>
                              <label htmlFor="joiningDate" className="block text-sm font-medium text-gray-700">
                                {t('employee:joining_date')}
                              </label>
                              <input
                                type="date"
                                id="joiningDate"
                                value={formData.joiningDate}
                                onChange={(e) => setFormData({ ...formData, joiningDate: e.target.value })}
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                              />
                            </div>
                            <div>
                              <label htmlFor="probationPeriod" className="block text-sm font-medium text-gray-700">
                                {t('employee:probation_period_months')}
                              </label>
                              <select
                                id="probationPeriod"
                                value={formData.probationPeriod}
                                onChange={(e) => setFormData({ ...formData, probationPeriod: e.target.value })}
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                              >
                                <option value="3">3 {t('employee:months')}</option>
                                <option value="6">6 {t('employee:months')}</option>
                                <option value="12">12 {t('employee:months')}</option>
                              </select>
                            </div>
                            <div className="sm:col-span-2">
                              <label htmlFor="status" className="block text-sm font-medium text-gray-700">
                                {t('employee:status')}
                              </label>
                              <select
                                id="status"
                                value={formData.status}
                                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                              >
                                <option value="active">{t('employee:active')}</option>
                                <option value="inactive">{t('employee:inactive')}</option>
                                <option value="on-leave">{t('employee:on_leave')}</option>
                                <option value="terminated">{t('employee:terminated')}</option>
                              </select>
                            </div>
                          </div>
                        )}

                        {/* Compensation Tab */}
                        {activeTab === 'compensation' && (
                          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                            <div>
                              <label htmlFor="salary" className="block text-sm font-medium text-gray-700">
                                {t('employee:salary')} *
                              </label>
                              <input
                                type="number"
                                id="salary"
                                value={formData.salary}
                                onChange={(e) => setFormData({ ...formData, salary: e.target.value })}
                                required
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                placeholder="50000"
                              />
                            </div>
                            <div>
                              <label htmlFor="payFrequency" className="block text-sm font-medium text-gray-700">
                                {t('employee:pay_frequency')}
                              </label>
                              <select
                                id="payFrequency"
                                value={formData.payFrequency}
                                onChange={(e) => setFormData({ ...formData, payFrequency: e.target.value })}
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                              >
                                <option value="monthly">{t('employee:monthly')}</option>
                                <option value="weekly">{t('employee:weekly')}</option>
                                <option value="bi-weekly">{t('employee:bi_weekly')}</option>
                                <option value="annually">{t('employee:annually')}</option>
                              </select>
                            </div>
                            <div>
                              <label htmlFor="hourlyRate" className="block text-sm font-medium text-gray-700">
                                {t('employee:hourly_rate')}
                              </label>
                              <input
                                type="number"
                                id="hourlyRate"
                                value={formData.hourlyRate}
                                onChange={(e) => setFormData({ ...formData, hourlyRate: e.target.value })}
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                placeholder="25.00"
                              />
                            </div>
                            <div></div>
                            <div className="sm:col-span-2">
                              <h4 className="text-sm font-medium text-gray-900 mb-3">{t('employee:bank_details')}</h4>
                            </div>
                            <div>
                              <label htmlFor="bankName" className="block text-sm font-medium text-gray-700">
                                {t('employee:bank_name')}
                              </label>
                              <input
                                type="text"
                                id="bankName"
                                value={formData.bankName}
                                onChange={(e) => setFormData({ ...formData, bankName: e.target.value })}
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                              />
                            </div>
                            <div>
                              <label htmlFor="accountNumber" className="block text-sm font-medium text-gray-700">
                                {t('employee:account_number')}
                              </label>
                              <input
                                type="text"
                                id="accountNumber"
                                value={formData.accountNumber}
                                onChange={(e) => setFormData({ ...formData, accountNumber: e.target.value })}
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                              />
                            </div>
                            <div className="sm:col-span-2">
                              <label htmlFor="ifscCode" className="block text-sm font-medium text-gray-700">
                                {t('employee:ifsc_code')}
                              </label>
                              <input
                                type="text"
                                id="ifscCode"
                                value={formData.ifscCode}
                                onChange={(e) => setFormData({ ...formData, ifscCode: e.target.value })}
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                              />
                            </div>
                          </div>
                        )}

                        {/* Address Tab */}
                        {activeTab === 'address' && (
                          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                            <div className="sm:col-span-2">
                              <label htmlFor="street" className="block text-sm font-medium text-gray-700">
                                {t('employee:street_address')}
                              </label>
                              <input
                                type="text"
                                id="street"
                                value={formData.address.street}
                                onChange={(e) => setFormData({
                                  ...formData,
                                  address: { ...formData.address, street: e.target.value }
                                })}
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                              />
                            </div>
                            <div>
                              <label htmlFor="city" className="block text-sm font-medium text-gray-700">
                                {t('employee:city')}
                              </label>
                              <input
                                type="text"
                                id="city"
                                value={formData.address.city}
                                onChange={(e) => setFormData({
                                  ...formData,
                                  address: { ...formData.address, city: e.target.value }
                                })}
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                              />
                            </div>
                            <div>
                              <label htmlFor="state" className="block text-sm font-medium text-gray-700">
                                {t('employee:state')}
                              </label>
                              <input
                                type="text"
                                id="state"
                                value={formData.address.state}
                                onChange={(e) => setFormData({
                                  ...formData,
                                  address: { ...formData.address, state: e.target.value }
                                })}
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                              />
                            </div>
                            <div>
                              <label htmlFor="zipCode" className="block text-sm font-medium text-gray-700">
                                {t('employee:zip_code')}
                              </label>
                              <input
                                type="text"
                                id="zipCode"
                                value={formData.address.zipCode}
                                onChange={(e) => setFormData({
                                  ...formData,
                                  address: { ...formData.address, zipCode: e.target.value }
                                })}
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                              />
                            </div>
                            <div>
                              <label htmlFor="country" className="block text-sm font-medium text-gray-700">
                                {t('employee:country')}
                              </label>
                              <input
                                type="text"
                                id="country"
                                value={formData.address.country}
                                onChange={(e) => setFormData({
                                  ...formData,
                                  address: { ...formData.address, country: e.target.value }
                                })}
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                placeholder="United States"
                              />
                            </div>
                          </div>
                        )}

                        {/* Emergency Contact Tab */}
                        {activeTab === 'emergency' && (
                          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                            <div>
                              <label htmlFor="emergencyName" className="block text-sm font-medium text-gray-700">
                                {t('employee:emergency_contact_name')}
                              </label>
                              <input
                                type="text"
                                id="emergencyName"
                                value={formData.emergencyContact.name}
                                onChange={(e) => setFormData({
                                  ...formData,
                                  emergencyContact: { ...formData.emergencyContact, name: e.target.value }
                                })}
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                              />
                            </div>
                            <div>
                              <label htmlFor="emergencyRelationship" className="block text-sm font-medium text-gray-700">
                                {t('employee:relationship')}
                              </label>
                              <input
                                type="text"
                                id="emergencyRelationship"
                                value={formData.emergencyContact.relationship}
                                onChange={(e) => setFormData({
                                  ...formData,
                                  emergencyContact: { ...formData.emergencyContact, relationship: e.target.value }
                                })}
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                placeholder="Spouse, Parent, Sibling"
                              />
                            </div>
                            <div>
                              <label htmlFor="emergencyPhone" className="block text-sm font-medium text-gray-700">
                                {t('employee:emergency_phone')}
                              </label>
                              <input
                                type="tel"
                                id="emergencyPhone"
                                value={formData.emergencyContact.phone}
                                onChange={(e) => setFormData({
                                  ...formData,
                                  emergencyContact: { ...formData.emergencyContact, phone: e.target.value }
                                })}
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                              />
                            </div>
                            <div>
                              <label htmlFor="emergencyEmail" className="block text-sm font-medium text-gray-700">
                                {t('employee:emergency_email')}
                              </label>
                              <input
                                type="email"
                                id="emergencyEmail"
                                value={formData.emergencyContact.email}
                                onChange={(e) => setFormData({
                                  ...formData,
                                  emergencyContact: { ...formData.emergencyContact, email: e.target.value }
                                })}
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                              />
                            </div>
                          </div>
                        )}

                        {/* Additional Information Tab */}
                        {activeTab === 'additional' && (
                          <div className="space-y-6">
                            <div>
                              <label htmlFor="experience" className="block text-sm font-medium text-gray-700">
                                {t('employee:years_of_experience')}
                              </label>
                              <input
                                type="number"
                                id="experience"
                                value={formData.experience}
                                onChange={(e) => setFormData({ ...formData, experience: e.target.value })}
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                placeholder="5"
                              />
                            </div>
                            <div>
                              <label htmlFor="previousEmployer" className="block text-sm font-medium text-gray-700">
                                {t('employee:previous_employer')}
                              </label>
                              <input
                                type="text"
                                id="previousEmployer"
                                value={formData.previousEmployer}
                                onChange={(e) => setFormData({ ...formData, previousEmployer: e.target.value })}
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                              />
                            </div>
                            <div>
                              <label htmlFor="skills" className="block text-sm font-medium text-gray-700">
                                {t('employee:skills')}
                              </label>
                              <input
                                type="text"
                                id="skills"
                                value={formData.skills.join(', ')}
                                onChange={(e) => setFormData({
                                  ...formData,
                                  skills: e.target.value.split(',').map(skill => skill.trim()).filter(skill => skill)
                                })}
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                placeholder="JavaScript, React, Node.js"
                              />
                            </div>
                            <div>
                              <label htmlFor="qualifications" className="block text-sm font-medium text-gray-700">
                                {t('employee:qualifications')}
                              </label>
                              <textarea
                                id="qualifications"
                                value={formData.qualifications.join('\n')}
                                onChange={(e) => setFormData({
                                  ...formData,
                                  qualifications: e.target.value.split('\n').filter(q => q.trim())
                                })}
                                rows={3}
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                placeholder="Bachelor's in Computer Science&#10;MBA in Business Administration"
                              />
                            </div>
                            <div>
                              <label htmlFor="certifications" className="block text-sm font-medium text-gray-700">
                                {t('employee:certifications')}
                              </label>
                              <textarea
                                id="certifications"
                                value={formData.certifications.join('\n')}
                                onChange={(e) => setFormData({
                                  ...formData,
                                  certifications: e.target.value.split('\n').filter(c => c.trim())
                                })}
                                rows={3}
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                placeholder="AWS Certified Solutions Architect&#10;PMP Certification"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    {showEditModal ? t('common:update') : t('common:create')}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddModal(false);
                      setShowEditModal(false);
                      setActiveTab('basic');
                    }}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    {t('common:cancel')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Employee Details Modal */}
      {showDetailsModal && selectedEmployee && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            <div className="inline-block align-bottom bg-white rounded-2xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-5xl sm:w-full max-h-[90vh] overflow-y-auto">
              <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 p-8 text-white">
                {/* Header */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-6">
                    <div className="relative">
                      {selectedEmployee.photo ? (
                        <img
                          className="h-20 w-20 rounded-full object-cover border-4 border-white shadow-xl"
                          src={selectedEmployee.photo}
                          alt={`${selectedEmployee.firstName} ${selectedEmployee.lastName}`}
                        />
                      ) : (
                        <div className="h-20 w-20 rounded-full bg-white/20 backdrop-blur-sm border-4 border-white/50 flex items-center justify-center shadow-xl">
                          <span className="text-2xl font-bold text-white">
                            {selectedEmployee.firstName?.[0]}{selectedEmployee.lastName?.[0]}
                          </span>
                        </div>
                      )}
                      <div className="absolute -bottom-2 -right-2">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                          selectedEmployee.status === 'active'
                            ? 'bg-green-500/20 text-green-100 border border-green-400/30'
                            : selectedEmployee.status === 'on-leave'
                            ? 'bg-yellow-500/20 text-yellow-100 border border-yellow-400/30'
                            : 'bg-red-500/20 text-red-100 border border-red-400/30'
                        }`}>
                          {selectedEmployee.status === 'active'
                            ? t('employee:active')
                            : selectedEmployee.status === 'on-leave'
                            ? t('employee:on_leave')
                            : t('employee:inactive')}
                        </span>
                      </div>
                    </div>
                    <div>
                      <h1 className="text-3xl font-bold text-white">
                        {selectedEmployee.firstName} {selectedEmployee.lastName}
                      </h1>
                      <p className="text-lg text-white/90 mt-1">
                        {getDesignationName(selectedEmployee.designation) || selectedEmployee.position || 'N/A'}
                      </p>
                      <p className="text-sm text-white/70">
                        {selectedEmployee.employeeId || 'N/A'} • {getDepartmentName(selectedEmployee.department)}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowDetailsModal(false)}
                    className="text-white/70 hover:text-white hover:bg-white/10 rounded-full p-2 transition-all duration-200"
                  >
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="bg-white p-8">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Basic Information */}
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-100">
                    <div className="flex items-center mb-4">
                      <div className="p-2 bg-blue-100 rounded-lg mr-3">
                        <UserIcon className="h-6 w-6 text-blue-600" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900">{t('employee:basic_info')}</h3>
                    </div>
                    <dl className="space-y-3">
                      <div className="flex justify-between items-center py-2 border-b border-gray-100">
                        <dt className="text-sm font-medium text-gray-600">{t('employee:email')}</dt>
                        <dd className="text-sm text-gray-900">{selectedEmployee.email}</dd>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-gray-100">
                        <dt className="text-sm font-medium text-gray-600">{t('employee:phone')}</dt>
                        <dd className="text-sm text-gray-900">{selectedEmployee.phone || 'N/A'}</dd>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-gray-100">
                        <dt className="text-sm font-medium text-gray-600">{t('employee:date_of_birth')}</dt>
                        <dd className="text-sm text-gray-900">
                          {selectedEmployee.dateOfBirth ? new Date(selectedEmployee.dateOfBirth).toLocaleDateString() : 'N/A'}
                        </dd>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-gray-100">
                        <dt className="text-sm font-medium text-gray-600">{t('employee:gender')}</dt>
                        <dd className="text-sm text-gray-900">{selectedEmployee.gender || 'N/A'}</dd>
                      </div>
                      <div className="flex justify-between items-center py-2">
                        <dt className="text-sm font-medium text-gray-600">{t('employee:marital_status')}</dt>
                        <dd className="text-sm text-gray-900">{selectedEmployee.maritalStatus || 'N/A'}</dd>
                      </div>
                    </dl>
                  </div>

                  {/* Employment Details */}
                  <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-6 border border-purple-100">
                    <div className="flex items-center mb-4">
                      <div className="p-2 bg-purple-100 rounded-lg mr-3">
                        <BriefcaseIcon className="h-6 w-6 text-purple-600" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900">{t('employee:employment_details')}</h3>
                    </div>
                    <dl className="space-y-3">
                      <div className="flex justify-between items-center py-2 border-b border-gray-100">
                        <dt className="text-sm font-medium text-gray-600">{t('employee:department')}</dt>
                        <dd className="text-sm text-gray-900">{getDepartmentName(selectedEmployee.department)}</dd>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-gray-100">
                        <dt className="text-sm font-medium text-gray-600">{t('employee:designation')}</dt>
                        <dd className="text-sm text-gray-900">{getDesignationName(selectedEmployee.designation) || selectedEmployee.position || 'N/A'}</dd>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-gray-100">
                        <dt className="text-sm font-medium text-gray-600">{t('employee:employment_type')}</dt>
                        <dd className="text-sm text-gray-900">{selectedEmployee.employmentType || 'N/A'}</dd>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-gray-100">
                        <dt className="text-sm font-medium text-gray-600">{t('employee:manager')}</dt>
                        <dd className="text-sm text-gray-900">{selectedEmployee.manager || 'N/A'}</dd>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-gray-100">
                        <dt className="text-sm font-medium text-gray-600">{t('employee:work_location')}</dt>
                        <dd className="text-sm text-gray-900">{selectedEmployee.workLocation || 'N/A'}</dd>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-gray-100">
                        <dt className="text-sm font-medium text-gray-600">{t('employee:hire_date')}</dt>
                        <dd className="text-sm text-gray-900">
                          {selectedEmployee.hireDate ? new Date(selectedEmployee.hireDate).toLocaleDateString() : 'N/A'}
                        </dd>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-gray-100">
                        <dt className="text-sm font-medium text-gray-600">{t('employee:joining_date')}</dt>
                        <dd className="text-sm text-gray-900">
                          {selectedEmployee.joiningDate ? new Date(selectedEmployee.joiningDate).toLocaleDateString() : 'N/A'}
                        </dd>
                      </div>
                      <div className="flex justify-between items-center py-2">
                        <dt className="text-sm font-medium text-gray-600">{t('employee:probation_period')}</dt>
                        <dd className="text-sm text-gray-900">{selectedEmployee.probationPeriod ? `${selectedEmployee.probationPeriod} months` : 'N/A'}</dd>
                      </div>
                    </dl>
                  </div>

                  {/* Emergency Contact */}
                  {selectedEmployee.emergencyContact && (
                    <div className="bg-gradient-to-br from-red-50 to-pink-50 rounded-2xl p-6 border border-red-100">
                      <div className="flex items-center mb-4">
                        <div className="p-2 bg-red-100 rounded-lg mr-3">
                          <HeartIcon className="h-6 w-6 text-red-600" />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900">{t('employee:emergency_contact')}</h3>
                      </div>
                      <dl className="space-y-3">
                        <div className="flex justify-between items-center py-2 border-b border-gray-100">
                          <dt className="text-sm font-medium text-gray-600">{t('employee:emergency_contact_name')}</dt>
                          <dd className="text-sm text-gray-900">{selectedEmployee.emergencyContact.name || 'N/A'}</dd>
                        </div>
                        <div className="flex justify-between items-center py-2 border-b border-gray-100">
                          <dt className="text-sm font-medium text-gray-600">{t('employee:relationship')}</dt>
                          <dd className="text-sm text-gray-900">{selectedEmployee.emergencyContact.relationship || 'N/A'}</dd>
                        </div>
                        <div className="flex justify-between items-center py-2 border-b border-gray-100">
                          <dt className="text-sm font-medium text-gray-600">{t('employee:emergency_phone')}</dt>
                          <dd className="text-sm text-gray-900">{selectedEmployee.emergencyContact.phone || 'N/A'}</dd>
                        </div>
                        <div className="flex justify-between items-center py-2">
                          <dt className="text-sm font-medium text-gray-600">{t('employee:emergency_email')}</dt>
                          <dd className="text-sm text-gray-900">{selectedEmployee.emergencyContact.email || 'N/A'}</dd>
                        </div>
                      </dl>
                    </div>
                  )}

                  {/* Address */}
                  {selectedEmployee.address && (
                    <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-6 border border-green-100">
                      <div className="flex items-center mb-4">
                        <div className="p-2 bg-green-100 rounded-lg mr-3">
                          <MapPinIcon className="h-6 w-6 text-green-600" />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900">{t('employee:address')}</h3>
                      </div>
                      <div className="text-sm text-gray-900 space-y-1">
                        {selectedEmployee.address.street && <p className="font-medium">{selectedEmployee.address.street}</p>}
                        <p>
                          {selectedEmployee.address.city && `${selectedEmployee.address.city}, `}
                          {selectedEmployee.address.state && `${selectedEmployee.address.state} `}
                          {selectedEmployee.address.zipCode && selectedEmployee.address.zipCode}
                        </p>
                        {selectedEmployee.address.country && <p>{selectedEmployee.address.country}</p>}
                      </div>
                    </div>
                  )}

                  {/* Compensation Details */}
                  <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-6 border border-emerald-100">
                    <div className="flex items-center mb-4">
                      <div className="p-2 bg-emerald-100 rounded-lg mr-3">
                        <BanknotesIcon className="h-6 w-6 text-emerald-600" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900">{t('employee:compensation_details')}</h3>
                    </div>
                    <dl className="space-y-3">
                      {selectedEmployee.salary && typeof selectedEmployee.salary === 'object' ? (
                        // Display salary breakdown if available
                        <>
                          <div className="flex justify-between items-center py-2 border-b border-gray-100">
                            <dt className="text-sm font-medium text-gray-600">Gross Salary</dt>
                            <dd className="text-sm font-bold text-green-600">₹{selectedEmployee.salary.gross?.toLocaleString() || 0}</dd>
                          </div>
                          <div className="flex justify-between items-center py-2 border-b border-gray-100">
                            <dt className="text-sm font-medium text-gray-600">Basic Salary</dt>
                            <dd className="text-sm text-gray-900">₹{selectedEmployee.salary.basic?.toLocaleString() || 0}</dd>
                          </div>
                          <div className="flex justify-between items-center py-2 border-b border-gray-100">
                            <dt className="text-sm font-medium text-gray-600">HRA</dt>
                            <dd className="text-sm text-gray-900">₹{selectedEmployee.salary.hra?.toLocaleString() || 0}</dd>
                          </div>
                          {selectedEmployee.salary.allowances && selectedEmployee.salary.allowances.length > 0 && (
                            <div className="py-2 border-b border-gray-100">
                              <dt className="text-sm font-medium text-gray-600 mb-2">Allowances</dt>
                              <div className="space-y-1 ml-4">
                                {selectedEmployee.salary.allowances.map((allowance, index) => (
                                  <div key={index} className="flex justify-between text-sm">
                                    <span className="text-gray-600">{allowance.name}:</span>
                                    <span className="text-gray-900">₹{allowance.amount?.toLocaleString() || 0}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          {selectedEmployee.salary.deductions && (
                            <div className="py-2 border-b border-gray-100">
                              <dt className="text-sm font-medium text-gray-600 mb-2">Deductions</dt>
                              <div className="space-y-1 ml-4">
                                {selectedEmployee.salary.deductions.pf?.employee > 0 && (
                                  <div className="flex justify-between text-sm">
                                    <span className="text-gray-600">PF (Employee):</span>
                                    <span className="text-red-600">₹{selectedEmployee.salary.deductions.pf.employee?.toLocaleString() || 0}</span>
                                  </div>
                                )}
                                {selectedEmployee.salary.deductions.esi?.employee > 0 && (
                                  <div className="flex justify-between text-sm">
                                    <span className="text-gray-600">ESI (Employee):</span>
                                    <span className="text-red-600">₹{selectedEmployee.salary.deductions.esi.employee?.toLocaleString() || 0}</span>
                                  </div>
                                )}
                                {selectedEmployee.salary.deductions.professionalTax > 0 && (
                                  <div className="flex justify-between text-sm">
                                    <span className="text-gray-600">Professional Tax:</span>
                                    <span className="text-red-600">₹{selectedEmployee.salary.deductions.professionalTax?.toLocaleString() || 0}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                          <div className="flex justify-between items-center py-2 border-b border-gray-100">
                            <dt className="text-sm font-medium text-gray-600">Net Salary</dt>
                            <dd className="text-sm font-bold text-blue-600">₹{selectedEmployee.salary.netSalary?.toLocaleString() || 0}</dd>
                          </div>
                          <div className="flex justify-between items-center py-2">
                            <dt className="text-sm font-medium text-gray-600">CTC</dt>
                            <dd className="text-sm font-bold text-purple-600">₹{selectedEmployee.salary.ctc?.toLocaleString() || 0}</dd>
                          </div>
                        </>
                      ) : (
                        // Display simple salary if no breakdown available
                        <>
                          <div className="flex justify-between items-center py-2 border-b border-gray-100">
                            <dt className="text-sm font-medium text-gray-600">{t('employee:salary')}</dt>
                            <dd className="text-sm font-bold text-green-600">
                              {selectedEmployee.salary ? `₹${selectedEmployee.salary.toLocaleString()}` : 'N/A'}
                            </dd>
                          </div>
                          <div className="flex justify-between items-center py-2 border-b border-gray-100">
                            <dt className="text-sm font-medium text-gray-600">{t('employee:hourly_rate')}</dt>
                            <dd className="text-sm text-gray-900">
                              {selectedEmployee.hourlyRate ? `₹${selectedEmployee.hourlyRate}` : 'N/A'}
                            </dd>
                          </div>
                          <div className="flex justify-between items-center py-2">
                            <dt className="text-sm font-medium text-gray-600">{t('employee:pay_frequency')}</dt>
                            <dd className="text-sm text-gray-900">{selectedEmployee.payFrequency || 'N/A'}</dd>
                          </div>
                        </>
                      )}
                    </dl>
                  </div>

                  {/* Tax Information */}
                  {selectedEmployee.taxInfo && (
                    <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-6 border border-amber-100">
                      <div className="flex items-center mb-4">
                        <div className="p-2 bg-amber-100 rounded-lg mr-3">
                          <IdentificationIcon className="h-6 w-6 text-amber-600" />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900">{t('employee:tax_information')}</h3>
                      </div>
                      <dl className="space-y-3">
                        <div className="flex justify-between items-center py-2 border-b border-gray-100">
                          <dt className="text-sm font-medium text-gray-600">{t('employee:pan')}</dt>
                          <dd className="text-sm text-gray-900 font-mono">{selectedEmployee.taxInfo.pan || 'N/A'}</dd>
                        </div>
                        <div className="flex justify-between items-center py-2 border-b border-gray-100">
                          <dt className="text-sm font-medium text-gray-600">{t('employee:tax_regime')}</dt>
                          <dd className="text-sm text-gray-900">{selectedEmployee.taxInfo.taxRegime || 'N/A'}</dd>
                        </div>
                        <div className="flex justify-between items-center py-2 border-b border-gray-100">
                          <dt className="text-sm font-medium text-gray-600">{t('employee:pf_number')}</dt>
                          <dd className="text-sm text-gray-900 font-mono">{selectedEmployee.taxInfo.pfNumber || 'N/A'}</dd>
                        </div>
                        <div className="flex justify-between items-center py-2 border-b border-gray-100">
                          <dt className="text-sm font-medium text-gray-600">{t('employee:esi_number')}</dt>
                          <dd className="text-sm text-gray-900 font-mono">{selectedEmployee.taxInfo.esiNumber || 'N/A'}</dd>
                        </div>
                        <div className="flex justify-between items-center py-2 border-b border-gray-100">
                          <dt className="text-sm font-medium text-gray-600">{t('employee:uan_number')}</dt>
                          <dd className="text-sm text-gray-900 font-mono">{selectedEmployee.taxInfo.uanNumber || 'N/A'}</dd>
                        </div>
                        <div className="flex justify-between items-center py-2">
                          <dt className="text-sm font-medium text-gray-600">TDS Applicable</dt>
                          <dd className="text-sm">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              selectedEmployee.taxInfo.tdsApplicable === true 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {selectedEmployee.taxInfo.tdsApplicable === true ? 'Yes' : 'No'}
                            </span>
                          </dd>
                        </div>
                      </dl>
                    </div>
                  )}

                  {/* Bank Details */}
                  {selectedEmployee.bankName && (
                    <div className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-2xl p-6 border border-cyan-100">
                      <div className="flex items-center mb-4">
                        <div className="p-2 bg-cyan-100 rounded-lg mr-3">
                          <CreditCardIcon className="h-6 w-6 text-cyan-600" />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900">{t('employee:bank_details')}</h3>
                      </div>
                      <dl className="space-y-3">
                        <div className="flex justify-between items-center py-2 border-b border-gray-100">
                          <dt className="text-sm font-medium text-gray-600">{t('employee:bank_name')}</dt>
                          <dd className="text-sm text-gray-900">{selectedEmployee.bankName}</dd>
                        </div>
                        <div className="flex justify-between items-center py-2 border-b border-gray-100">
                          <dt className="text-sm font-medium text-gray-600">{t('employee:account_number')}</dt>
                          <dd className="text-sm text-gray-900 font-mono">{selectedEmployee.accountNumber || 'N/A'}</dd>
                        </div>
                        <div className="flex justify-between items-center py-2">
                          <dt className="text-sm font-medium text-gray-600">{t('employee:ifsc_code')}</dt>
                          <dd className="text-sm text-gray-900 font-mono">{selectedEmployee.ifscCode || 'N/A'}</dd>
                        </div>
                      </dl>
                    </div>
                  )}

                  {/* Branch & Shift Information */}
                  {(selectedEmployee.branch || selectedEmployee.shiftId) && (
                    <div className="bg-gradient-to-br from-violet-50 to-purple-50 rounded-2xl p-6 border border-violet-100">
                      <div className="flex items-center mb-4">
                        <div className="p-2 bg-violet-100 rounded-lg mr-3">
                          <BuildingStorefrontIcon className="h-6 w-6 text-violet-600" />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900">{t('employee:branch_shift_info')}</h3>
                      </div>
                      <dl className="space-y-3">
                        {selectedEmployee.branch && (
                          <div className="flex justify-between items-center py-2 border-b border-gray-100">
                            <dt className="text-sm font-medium text-gray-600">{t('employee:branch')}</dt>
                            <dd className="text-sm text-gray-900">
                              {getBranchName(selectedEmployee.branch)}
                            </dd>
                          </div>
                        )}
                        {selectedEmployee.shiftId && (
                          <div className="flex justify-between items-center py-2">
                            <dt className="text-sm font-medium text-gray-600">{t('employee:shift')}</dt>
                            <dd className="text-sm text-gray-900">
                              {getShiftName(selectedEmployee.shiftId)}
                            </dd>
                          </div>
                        )}
                      </dl>
                    </div>
                  )}

                  {/* TDS Declaration */}
                  {selectedEmployee.taxInfo?.tdsApplicable === true && selectedEmployee.taxInfo?.tdsDeclaration && (
                    <div className="bg-gradient-to-br from-rose-50 to-pink-50 rounded-2xl p-6 border border-rose-100">
                      <div className="flex items-center mb-4">
                        <div className="p-2 bg-rose-100 rounded-lg mr-3">
                          <ShieldCheckIcon className="h-6 w-6 text-rose-600" />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900">{t('employee:tds_declaration')}</h3>
                      </div>
                      <dl className="space-y-3">
                        <div className="flex justify-between items-center py-2 border-b border-gray-100">
                          <dt className="text-sm font-medium text-gray-600">{t('employee:is_declared')}</dt>
                          <dd className="text-sm text-gray-900">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              selectedEmployee.taxInfo.tdsDeclaration.isDeclared
                                ? 'bg-green-100 text-green-800'
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {selectedEmployee.taxInfo.tdsDeclaration.isDeclared ? 'Yes' : 'No'}
                            </span>
                          </dd>
                        </div>
                        <div className="flex justify-between items-center py-2 border-b border-gray-100">
                          <dt className="text-sm font-medium text-gray-600">{t('employee:declaration_type')}</dt>
                          <dd className="text-sm text-gray-900">{getDeclarationTypeLabel(selectedEmployee.taxInfo.tdsDeclaration.declarationType)}</dd>
                        </div>
                        <div className="flex justify-between items-center py-2 border-b border-gray-100">
                          <dt className="text-sm font-medium text-gray-600">Approval Status</dt>
                          <dd className="text-sm text-gray-900">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              selectedEmployee.taxInfo.tdsDeclaration.status === 'approved'
                                ? 'bg-green-100 text-green-800'
                                : selectedEmployee.taxInfo.tdsDeclaration.status === 'rejected'
                                ? 'bg-red-100 text-red-800'
                                : selectedEmployee.taxInfo.tdsDeclaration.status === 'expired'
                                ? 'bg-gray-100 text-gray-800'
                                : 'bg-yellow-100 text-yellow-800'
                            }`}>
                              {selectedEmployee.taxInfo.tdsDeclaration.status || 'pending'}
                            </span>
                          </dd>
                        </div>
                        <div className="flex justify-between items-center py-2">
                          <dt className="text-sm font-medium text-gray-600">{t('employee:reason')}</dt>
                          <dd className="text-sm text-gray-900">{getReasonLabel(selectedEmployee.taxInfo.tdsDeclaration.reason)}</dd>
                        </div>
                        {selectedEmployee.taxInfo.tdsDeclaration.remarks && (
                          <div className="mt-3">
                            <dt className="text-sm font-medium text-gray-600 mb-1">{t('employee:remarks')}</dt>
                            <dd className="text-sm text-gray-900 bg-gray-50 p-2 rounded-lg">{selectedEmployee.taxInfo.tdsDeclaration.remarks}</dd>
                          </div>
                        )}
                        {selectedEmployee.taxInfo.tdsDeclaration.approvalComment && (
                          <div className="mt-3">
                            <dt className="text-sm font-medium text-gray-600 mb-1">Approval Comment</dt>
                            <dd className="text-sm text-gray-900 bg-blue-50 p-2 rounded-lg border border-blue-200">{selectedEmployee.taxInfo.tdsDeclaration.approvalComment}</dd>
                          </div>
                        )}
                        {selectedEmployee.taxInfo.tdsDeclaration.approvedAt && (
                          <div className="mt-3">
                            <dt className="text-sm font-medium text-gray-600 mb-1">Approved Date</dt>
                            <dd className="text-sm text-gray-900">{new Date(selectedEmployee.taxInfo.tdsDeclaration.approvedAt).toLocaleDateString()}</dd>
                          </div>
                        )}
                        {selectedEmployee.taxInfo.tdsDeclaration.validFrom && selectedEmployee.taxInfo.tdsDeclaration.validTo && (
                          <div className="mt-3">
                            <dt className="text-sm font-medium text-gray-600 mb-1">Valid Period</dt>
                            <dd className="text-sm text-gray-900">
                              {new Date(selectedEmployee.taxInfo.tdsDeclaration.validFrom).toLocaleDateString()} - {new Date(selectedEmployee.taxInfo.tdsDeclaration.validTo).toLocaleDateString()}
                            </dd>
                          </div>
                        )}
                      </dl>
                    </div>
                  )}

                  {/* Additional Information */}
                  {(selectedEmployee.experience || selectedEmployee.previousEmployer || selectedEmployee.qualifications?.length > 0 || selectedEmployee.certifications?.length > 0) && (
                    <div className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-2xl p-6 border border-slate-100">
                      <div className="flex items-center mb-4">
                        <div className="p-2 bg-slate-100 rounded-lg mr-3">
                          <AcademicCapIcon className="h-6 w-6 text-slate-600" />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900">{t('employee:additional_info')}</h3>
                      </div>
                      <dl className="space-y-3">
                        {selectedEmployee.experience && (
                          <div className="flex justify-between items-center py-2 border-b border-gray-100">
                            <dt className="text-sm font-medium text-gray-600">{t('employee:years_of_experience')}</dt>
                            <dd className="text-sm text-gray-900">{selectedEmployee.experience} years</dd>
                          </div>
                        )}
                        {selectedEmployee.previousEmployer && (
                          <div className="flex justify-between items-center py-2 border-b border-gray-100">
                            <dt className="text-sm font-medium text-gray-600">{t('employee:previous_employer')}</dt>
                            <dd className="text-sm text-gray-900">{selectedEmployee.previousEmployer}</dd>
                          </div>
                        )}
                        {selectedEmployee.qualifications && selectedEmployee.qualifications.length > 0 && (
                          <div className="py-2 border-b border-gray-100">
                            <dt className="text-sm font-medium text-gray-600 mb-2">{t('employee:qualifications')}</dt>
                            <dd className="text-sm text-gray-900">
                              <ul className="list-disc list-inside space-y-1">
                                {selectedEmployee.qualifications.map((qualification, index) => (
                                  <li key={index}>{qualification}</li>
                                ))}
                              </ul>
                            </dd>
                          </div>
                        )}
                        {selectedEmployee.certifications && selectedEmployee.certifications.length > 0 && (
                          <div className="py-2">
                            <dt className="text-sm font-medium text-gray-600 mb-2">{t('employee:certifications')}</dt>
                            <dd className="text-sm text-gray-900">
                              <ul className="list-disc list-inside space-y-1">
                                {selectedEmployee.certifications.map((certification, index) => (
                                  <li key={index}>{certification}</li>
                                ))}
                              </ul>
                            </dd>
                          </div>
                        )}
                      </dl>
                    </div>
                  )}

                  {/* Skills & Qualifications */}
                  <div className="lg:col-span-2 bg-gradient-to-br from-indigo-50 to-blue-50 rounded-2xl p-6 border border-indigo-100">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {selectedEmployee.skills && selectedEmployee.skills.length > 0 && (
                        <div>
                          <div className="flex items-center mb-4">
                            <div className="p-2 bg-indigo-100 rounded-lg mr-3">
                              <DocumentIcon className="h-6 w-6 text-indigo-600" />
                            </div>
                            <h4 className="text-lg font-semibold text-gray-900">{t('employee:skills')}</h4>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {selectedEmployee.skills.map((skill, index) => (
                              <span key={index} className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-indigo-100 text-indigo-800 border border-indigo-200">
                                {skill}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {selectedEmployee.qualifications && selectedEmployee.qualifications.length > 0 && (
                        <div>
                          <div className="flex items-center mb-4">
                            <div className="p-2 bg-blue-100 rounded-lg mr-3">
                              <AcademicCapIcon className="h-6 w-6 text-blue-600" />
                            </div>
                            <h4 className="text-lg font-semibold text-gray-900">{t('employee:qualifications')}</h4>
                          </div>
                          <ul className="space-y-2">
                            {selectedEmployee.qualifications.map((qualification, index) => (
                              <li key={index} className="flex items-start text-sm text-gray-900">
                                <span className="inline-block w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                                {qualification}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {selectedEmployee.certifications && selectedEmployee.certifications.length > 0 && (
                        <div>
                          <div className="flex items-center mb-4">
                            <div className="p-2 bg-green-100 rounded-lg mr-3">
                              <ShieldCheckIcon className="h-6 w-6 text-green-600" />
                            </div>
                            <h4 className="text-lg font-semibold text-gray-900">{t('employee:certifications')}</h4>
                          </div>
                          <ul className="space-y-2">
                            {selectedEmployee.certifications.map((certification, index) => (
                              <li key={index} className="flex items-start text-sm text-gray-900">
                                <span className="inline-block w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                                {certification}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {(selectedEmployee.experience || selectedEmployee.previousEmployer) && (
                        <div>
                          <div className="flex items-center mb-4">
                            <div className="p-2 bg-purple-100 rounded-lg mr-3">
                              <BriefcaseIcon className="h-6 w-6 text-purple-600" />
                            </div>
                            <h4 className="text-lg font-semibold text-gray-900">{t('employee:experience')}</h4>
                          </div>
                          <dl className="space-y-2">
                            {selectedEmployee.experience && (
                              <div className="flex justify-between items-center py-1">
                                <dt className="text-sm font-medium text-gray-600">{t('employee:years_of_experience')}</dt>
                                <dd className="text-sm text-gray-900">{selectedEmployee.experience} years</dd>
                              </div>
                            )}
                            {selectedEmployee.previousEmployer && (
                              <div className="flex justify-between items-center py-1">
                                <dt className="text-sm font-medium text-gray-600">{t('employee:previous_employer')}</dt>
                                <dd className="text-sm text-gray-900">{selectedEmployee.previousEmployer}</dd>
                              </div>
                            )}
                          </dl>
                        </div>
                      )}
                    </div>

                    {/* References */}
                    {selectedEmployee.references && selectedEmployee.references.length > 0 && (
                      <div className="mt-6 pt-6 border-t border-indigo-200">
                        <div className="flex items-center mb-4">
                          <div className="p-2 bg-indigo-100 rounded-lg mr-3">
                            <UserIcon className="h-6 w-6 text-indigo-600" />
                          </div>
                          <h4 className="text-lg font-semibold text-gray-900">{t('employee:references')}</h4>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {selectedEmployee.references.map((reference, index) => (
                            <div key={index} className="bg-white/50 rounded-lg p-4 border border-indigo-200">
                              <h5 className="font-medium text-gray-900">{reference.name}</h5>
                              <p className="text-sm text-gray-600">{reference.position}</p>
                              <p className="text-sm text-gray-600">{reference.company}</p>
                              <p className="text-sm text-gray-600">{reference.phone}</p>
                              <p className="text-sm text-gray-600">{reference.email}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-8 py-6 border-t border-gray-200">
                <div className="flex justify-end space-x-4">
                  <button
                    onClick={() => setShowDetailsModal(false)}
                    className="inline-flex items-center px-6 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transform hover:scale-105 transition-all duration-200 shadow-md hover:shadow-lg"
                  >
                    {t('common:close')}
                  </button>
                  <button
                    onClick={() => {
                      handleEditEmployee(selectedEmployee);
                      setShowDetailsModal(false);
                    }}
                    className="inline-flex items-center px-6 py-3 border border-transparent text-white bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl"
                  >
                    <PencilIcon className="-ml-1 mr-2 h-5 w-5" />
                    {t('employee:edit_employee')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedEmployee && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <TrashIcon className="h-6 w-6 text-red-600" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      {t('employee:delete_employee')}
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        {t('employee:delete_confirm_message', {
                          name: `${selectedEmployee.firstName} ${selectedEmployee.lastName}`
                        })}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={handleConfirmDelete}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  {t('employee:delete_employee')}
                </button>
                <button
                  type="button"
                  onClick={() => setShowDeleteModal(false)}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  {t('common:cancel')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TDS Update Modal */}
      {showTdsModal && selectedEmployee && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
              <form onSubmit={handleTdsSubmit}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-amber-100 sm:mx-0 sm:h-10 sm:w-10">
                      <IdentificationIcon className="h-6 w-6 text-amber-600" />
                    </div>
                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                      <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                        Update TDS Declaration
                      </h3>
                      <p className="text-sm text-gray-600 mb-6">
                        Update TDS declaration for <strong>{selectedEmployee.firstName} {selectedEmployee.lastName}</strong> (ID: {selectedEmployee.employeeId})
                      </p>

                      <div className="space-y-6">
                        {/* TDS Declaration Checkbox */}
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            checked={tdsFormData.isDeclared}
                            onChange={(e) => setTdsFormData(prev => ({
                              ...prev,
                              isDeclared: e.target.checked
                            }))}
                            className="h-4 w-4 text-amber-600 focus:ring-amber-500 border-gray-300 rounded"
                          />
                          <label className="ml-2 text-sm font-medium text-gray-700">
                            I declare that I do not want TDS to be deducted from my salary
                          </label>
                        </div>

                        {/* TDS Status - Only for Company Admin */}
                        {user?.role === 'company_admin' && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Approval Status
                            </label>
                            <select
                              value={tdsFormData.status}
                              onChange={(e) => setTdsFormData(prev => ({
                                ...prev,
                                status: e.target.value
                              }))}
                              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-200"
                            >
                              <option value="pending">Pending</option>
                              <option value="approved">Approved</option>
                              <option value="rejected">Rejected</option>
                              <option value="expired">Expired</option>
                            </select>
                          </div>
                        )}

                        {/* Approval Comment - Only for Company Admin when approving/rejecting */}
                        {user?.role === 'company_admin' && (tdsFormData.status === 'approved' || tdsFormData.status === 'rejected') && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Approval Comment
                            </label>
                            <textarea
                              value={tdsFormData.approvalComment}
                              onChange={(e) => setTdsFormData(prev => ({
                                ...prev,
                                approvalComment: e.target.value
                              }))}
                              rows={3}
                              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-200 resize-none"
                              placeholder={tdsFormData.status === 'approved' ? 'Add approval comments...' : 'Add rejection reason...'}
                              required={tdsFormData.status === 'approved' || tdsFormData.status === 'rejected'}
                            />
                          </div>
                        )}

                        {/* TDS Declaration Details */}
                        {tdsFormData.isDeclared && (
                          <div className="space-y-4 bg-amber-50 p-4 rounded-lg border border-amber-200">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Declaration Type
                              </label>
                              <select
                                value={tdsFormData.declarationType}
                                onChange={(e) => setTdsFormData(prev => ({
                                  ...prev,
                                  declarationType: e.target.value
                                }))}
                                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-200"
                              >
                                <option value="no_tds">No TDS Deduction</option>
                                <option value="lower_tds">Lower TDS Rate</option>
                                <option value="higher_tds">Higher TDS Rate</option>
                              </select>
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Reason for Declaration
                              </label>
                              <select
                                value={tdsFormData.reason}
                                onChange={(e) => setTdsFormData(prev => ({
                                  ...prev,
                                  reason: e.target.value
                                }))}
                                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-200"
                              >
                                <option value="no_income">No Other Income</option>
                                <option value="below_threshold">Income Below Threshold</option>
                                <option value="other">Other</option>
                              </select>
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Remarks/Additional Information
                              </label>
                              <textarea
                                value={tdsFormData.remarks}
                                onChange={(e) => setTdsFormData(prev => ({
                                  ...prev,
                                  remarks: e.target.value
                                }))}
                                rows={3}
                                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-200 resize-none"
                                placeholder="Please provide any additional information or supporting details for your TDS declaration..."
                              />
                            </div>
                          </div>
                        )}

                        {/* Important Notice */}
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                          <div className="flex">
                            <div className="flex-shrink-0">
                              <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                              </svg>
                            </div>
                            <div className="ml-3">
                              <h4 className="text-sm font-medium text-blue-800">Important Notice</h4>
                              <div className="mt-2 text-sm text-blue-700">
                                <p>This declaration will be reviewed by the HR/Finance team. You may be required to submit supporting documents.</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    disabled={isUpdatingTds}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-amber-600 text-base font-medium text-white hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 disabled:opacity-50 disabled:cursor-not-allowed sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    {isUpdatingTds ? 'Updating...' : user?.role === 'company_admin' && tdsFormData.status === 'approved' ? 'Approve TDS Declaration' : user?.role === 'company_admin' && tdsFormData.status === 'rejected' ? 'Reject TDS Declaration' : 'Update TDS Declaration'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowTdsModal(false);
                      setSelectedEmployee(null);
                    }}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    {t('common:cancel')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      </>
    </>
  );
};

export default Employees;
